import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { environment } from "../../../../Environments/Environment";
import { PartnerSearchResponse } from "../Dto/PartnerSearchResponse";
import { PartnerSearchRequest } from "../Dto/PartnerSearchRequest";
import { Partner } from "../Dto/PartnerModel";
import { CreatePartnerRequest } from "../Dto/CreatePartnerRequest";
import { firstValueFrom } from "rxjs";
import { PartnerPublicRequestLink, PartnerPublicRequestLinkGenerateResponse } from "../Dto/PartnerPublicRequestLink";

@Injectable({ providedIn: "root" })
export class PartnerApi {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.adminServiceUrl;

  async searchPartners(params: PartnerSearchRequest): Promise<PartnerSearchResponse> {
    const url = new URL("partners", `${this.baseUrl}/`);
    const searchParams = new URLSearchParams();

    if (params.search) {
      searchParams.set("search", params.search);
    }

    if (params.type) {
      searchParams.set("type", params.type);
    }

    if (params.page !== undefined) {
      searchParams.set("page", params.page.toString());
    }

    if (params.limit !== undefined) {
      searchParams.set("limit", params.limit.toString());
    }

    if (params.createdAtFrom) {
      searchParams.set("createdAtFrom", params.createdAtFrom);
    }

    if (params.createdAtTo) {
      searchParams.set("createdAtTo", params.createdAtTo);
    }

    if (searchParams.toString()) {
      url.search = searchParams.toString();
    }

    return await firstValueFrom(this.http.get<PartnerSearchResponse>(url.toString(), { withCredentials: true }));
  }

  async deletePartner(partnerId: string): Promise<void> {
    const url = new URL(`partners/${partnerId}`, `${this.baseUrl}/`);
    await firstValueFrom(this.http.delete<void>(url.toString(), { withCredentials: true }));
  }

  async createPartner(payload: CreatePartnerRequest): Promise<Partner> {
    const url = new URL("partners", `${this.baseUrl}/`);
    return await firstValueFrom(this.http.post<Partner>(url.toString(), payload, { withCredentials: true }));
  }

  async updatePartner(id: string, payload: Partial<CreatePartnerRequest>): Promise<Partner | null> {
    const url = new URL(`partners/${id}`, `${this.baseUrl}/`);
    return await firstValueFrom(this.http.put<Partner>(url.toString(), payload, { withCredentials: true }));
  }

  async getPartnerById(id: string): Promise<Partner | null> {
    const url = new URL(`partners/${id}`, `${this.baseUrl}/`);
    return await firstValueFrom(this.http.get<Partner>(url.toString(), { withCredentials: true }));
  }

  async getPublicRequestLink(partnerId: string): Promise<PartnerPublicRequestLink> {
    const url = new URL(`partners/${partnerId}/public-request-link`, `${this.baseUrl}/`);
    return await firstValueFrom(this.http.get<PartnerPublicRequestLink>(url.toString(), { withCredentials: true }));
  }

  async generatePublicRequestLink(partnerId: string, warehouseId: string): Promise<PartnerPublicRequestLinkGenerateResponse> {
    const url = new URL(`partners/${partnerId}/public-request-link`, `${this.baseUrl}/`);
    return await firstValueFrom(
      this.http.post<PartnerPublicRequestLinkGenerateResponse>(url.toString(), { warehouseId }, { withCredentials: true }),
    );
  }

  async disablePublicRequestLink(partnerId: string): Promise<PartnerPublicRequestLink> {
    const url = new URL(`partners/${partnerId}/public-request-link`, `${this.baseUrl}/`);
    return await firstValueFrom(this.http.delete<PartnerPublicRequestLink>(url.toString(), { withCredentials: true }));
  }
}
